import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, Badge } from '@noor/ui';

/**
 * The seven Courier-Detail sections, in Figma display order (node 83:1210
 * "Navigation Bar"). This is the URL search-param value the route owns, so the
 * tuple is the single source of truth for both the pill nav here and the
 * route's `validateSearch` tab enum — export it from the barrel and reuse it
 * rather than re-declaring the literals.
 */
export const COURIER_TABS = ['profile', 'orders', 'equipment'] as const;

export type CourierTab = (typeof COURIER_TABS)[number];

/**
 * Optional per-tab count, keyed by tab. A tab with a positive count renders a
 * count `Badge` after its label (e.g. the Orders/Debts totals); tabs without an
 * entry — or with `0`/`undefined` — render label-only. Conditional so
 * `exactOptionalPropertyTypes` stays happy: only pass the keys you have.
 */
export type CourierTabCounts = Partial<Record<CourierTab, number>>;

interface CourierTabsProps {
  /** The active section (URL-driven from the page). */
  activeTab: CourierTab;
  /** Fired when a pill is selected; the page pushes it into the URL. */
  onTabChange: (tab: CourierTab) => void;
  /** Optional counts rendered as count badges on their tab. */
  counts?: CourierTabCounts;
}

/**
 * Courier detail navigation uses the same understated underline treatment as
 * the courier list. The row scrolls horizontally on smaller screens.
 *
 * Purely presentational and controlled: `activeTab` + `onTabChange` come from
 * the page, which mirrors the tab into the route search params. Labels resolve
 * against the `courier` namespace (`tabs.<tab>`).
 */
export function CourierTabs({ activeTab, onTabChange, counts }: CourierTabsProps) {
  const { t } = useTranslation('courier');

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => onTabChange(value as CourierTab)}
      className="min-w-0"
    >
      <TabsList
        aria-label={t('tabsLabel')}
        className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none border-b border-border bg-transparent p-0"
      >
        {COURIER_TABS.map((tab) => {
          const count = counts?.[tab];
          const isActive = tab === activeTab;
          return (
            <TabsTrigger
              key={tab}
              value={tab}
              className="relative h-auto flex-none gap-1.5 rounded-none px-4 py-3 text-sm font-medium text-muted-foreground after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-transparent hover:text-foreground data-[state=active]:rounded-none data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:after:bg-status-active"
            >
              {t(`tabs.${tab}`)}
              {count !== undefined && count > 0 ? (
                <Badge
                  shape="count"
                  variant={isActive ? 'secondary' : 'default'}
                  className="ml-0.5"
                >
                  {count}
                </Badge>
              ) : null}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
